import React, { type ReactNode, Component } from 'react';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import StyleMarshalProvider from '../../style-marshal/provider/index.tsx';
import type { ClassName } from '../../style-marshal/types.tsx';
import withStyleMarshal, {
	type StyleMarshalContext,
} from '../../style-marshal/with-style-marshal/index.tsx';
import * as classNames from '../classnames';
import { Provider, type RoadmapStyleMarshalProps } from '../context';
import * as styles from '../styles';
import type { CustomMarshal } from '../types';

export type Overrides = Partial<RoadmapStyleMarshalProps>;

type Props = {
	/**
	 * custom style marshal handlers
	 * will be passed as part of regular style marshal props
	 */
	custom: CustomMarshal;
	/**
	 * Overrides for core style marshal functions
	 */
	overrides: Overrides;
	children: ReactNode;
} & StyleMarshalContext;

// eslint-disable-next-line jira/react/no-class-components
export class RoadmapStyleMarshalProvider extends Component<Props> {
	static defaultProps = {
		custom: {},
		overrides: {},
	};

	constructor(props: Props) {
		super(props);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const custom: Record<string, any> = {};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const overrides: Record<string, any> = {};

		/**
		 * binding happens in the constructor as we don't expect these function
		 * to change during execution
		 */
		Object.keys(props.custom).forEach((key) => {
			/**
			 * bind custom marshal handlers to component instance
			 * reminder, that all handlers must be regular "function"s, rather than lambdas
			 * to make it work
			 */
			custom[key] = props.custom[key].bind(this);
		});

		Object.keys(props.overrides).forEach((key) => {
			// bind overrides functions
			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<RoadmapStyleMarshalProps>'.
			overrides[key] = props.overrides[key].bind(this);
		});

		this.roadmapContext = {
			onRowMouseEnter: this.onRowMouseEnter,
			onRowMouseLeave: this.onRowMouseLeave,
			onRowSelect: this.onRowSelect,
			onRowDeselect: this.onRowDeselect,
			...overrides,
			customStyleMarshal: custom,
		};
	}

	onRowMouseEnter = (id: IssueId) => {
		if (id !== this.selectedIssueId) {
			this.insertStyle(classNames.row(id), styles.itemHoverStyles);
		}
	};

	onRowMouseLeave = (id: IssueId) => {
		if (id !== this.selectedIssueId) {
			this.removeStyle(classNames.row(id));
		}
	};

	onRowSelect = (id: IssueId) => {
		this.selectedIssueId = id;
		this.insertStyle(classNames.row(id), styles.itemSelectedStyles);
	};

	onRowDeselect = (id: IssueId) => {
		if (this.selectedIssueId === id) {
			this.selectedIssueId = undefined;
		}
		this.removeStyle(classNames.row(id));
	};

	insertStyle(className: ClassName, rule: string) {
		this.props.insertStyle(className, rule);
	}

	removeStyle(className: ClassName) {
		this.props.removeStyle(className);
	}

	selectedIssueId: IssueId | undefined = undefined;

	roadmapContext: RoadmapStyleMarshalProps;

	render() {
		const { children } = this.props;

		return <Provider value={this.roadmapContext}>{children}</Provider>;
	}
}

const WrappedRoadmapProvider = withStyleMarshal(RoadmapStyleMarshalProvider);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (props: {
	custom?: CustomMarshal;
	overrides?: Overrides;
	scope?: string;
	children: ReactNode;
}) => (
	<StyleMarshalProvider scope={props.scope}>
		{/* @ts-expect-error - TS2322 - Type 'Partial<RoadmapStyleMarshalProps> | undefined' is not assignable to type 'Partial<RoadmapStyleMarshalProps>'. | TS2322 - Type 'CustomMarshal | undefined' is not assignable to type 'CustomMarshal'. */}
		<WrappedRoadmapProvider overrides={props.overrides} custom={props.custom}>
			{props.children}
		</WrappedRoadmapProvider>
	</StyleMarshalProvider>
);
